/**
 * Ajudantes de DOM e de formatacao. Sem estado.
 *
 * Responsabilidade: criar elemento, formatar numero e formatar tempo — as tres
 * coisas que toda view faria de um jeito ligeiramente diferente se cada uma
 * fizesse a sua.
 *
 * NAO e responsabilidade deste arquivo: conhecer o dominio. Nada aqui sabe o
 * que e um aparelho, um setpoint ou um frame de timelapse.
 *
 * ## Por que `el()` e nao innerHTML
 *
 * Quase todo texto que esta PWA desenha vem de outra maquina: `dev_name` e
 * `apelido` sao digitados no painel local, `err` de um ack vem do firmware.
 * Com `innerHTML` qualquer um deles vira HTML. `textContent` (que e o que
 * `el()` usa) trata tudo como texto, e o problema deixa de existir em vez de
 * depender de alguem lembrar de escapar.
 */

type Filho = Node | string | number | null | undefined | false;

/**
 * Um painel da tela: monta o DOM uma vez, e redesenha quando o estado muda.
 *
 * Montar uma vez e nao a cada mudanca e o que preserva foco de input, posicao
 * de scroll e o `<img>` ja carregado do timelapse — recriar tudo a cada quadro
 * de telemetria (um por minuto, por aparelho) piscaria a tela inteira.
 */
export interface Painel {
  raiz: HTMLElement;
  atualizar: () => void;
}

interface Props {
  class?: string;
  text?: string | number;
  html?: never;
  [atributo: string]: unknown;
}

/** `el("div", { class: "card" }, "texto", outroNo)` */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...filhos: Filho[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [chave, valor] of Object.entries(props)) {
    if (valor === undefined || valor === null || valor === false) continue;
    if (chave === "class") node.className = String(valor);
    else if (chave === "text") node.textContent = String(valor);
    else if (chave.startsWith("on") && typeof valor === "function") {
      node.addEventListener(chave.slice(2), valor as EventListener);
    } else if (valor === true) node.setAttribute(chave, "");
    else node.setAttribute(chave, String(valor));
  }
  for (const filho of filhos) {
    if (filho === null || filho === undefined || filho === false) continue;
    node.append(typeof filho === "object" ? filho : String(filho));
  }
  return node;
}

export function svg(tag: string, props: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [chave, valor] of Object.entries(props)) {
    node.setAttribute(chave, String(valor));
  }
  return node;
}

export function trocar(alvo: HTMLElement, ...conteudo: Filho[]): void {
  alvo.replaceChildren(
    ...conteudo
      .filter((c): c is Node | string | number => c !== null && c !== undefined && c !== false)
      .map((c) => (typeof c === "object" ? c : String(c))),
  );
}

// --- formatacao --------------------------------------------------------

/** Traco em vez de zero: `null` significa "nunca foi lido" (contrato §4). */
export function num(valor: number | null | undefined, casas = 1): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";
  return valor.toFixed(casas);
}

export function inteiro(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";
  return String(Math.round(valor));
}

/** Minuto do dia → `HH:MM` (contrato §5, `setpoints.photo_on`). */
export function minutoDoDia(valor: number | null | undefined): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";
  const total = ((Math.round(valor) % 1440) + 1440) % 1440;
  const hora = String(Math.floor(total / 60)).padStart(2, "0");
  const minuto = String(total % 60).padStart(2, "0");
  return `${hora}:${minuto}`;
}

/** "agora", "há 4 min", "há 2 h", "há 3 d". Entrada em epoch ms. */
export function haQuanto(quando: number | null): string {
  if (quando === null) return "—";
  const segundos = Math.max(0, Math.round((Date.now() - quando) / 1000));
  if (segundos < 45) return "agora";
  if (segundos < 3600) return `há ${Math.round(segundos / 60)} min`;
  if (segundos < 86400) return `há ${Math.floor(segundos / 3600)} h`;
  return `há ${Math.floor(segundos / 86400)} d`;
}

export const relogio = (quando: number | null): string =>
  quando === null
    ? "—"
    : new Date(quando).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export function dataHora(iso: string | null): string {
  if (iso === null) return "—";
  const quando = Date.parse(iso);
  if (Number.isNaN(quando)) return "—";
  return new Date(quando).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Epoch em SEGUNDOS (o `ts` de `TelemetrySample`) → "DD/MM HH:MM".
 *
 * `dataHora()` recebe ISO e serve a maioria da tela; o grafico de periodo
 * escolhivel precisa formatar `ts` cru quando a janela passa de 24 h — so a
 * hora (`relogio()`) deixa de bastar quando o balão pode apontar para
 * qualquer um de 30 dias.
 */
export function dataHoraDeEpoch(segundos: number | null): string {
  if (segundos === null || !Number.isFinite(segundos)) return "—";
  return new Date(segundos * 1000).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Sparkline: uma polyline em viewBox 64x24, mesma forma do painel local.
 *
 * Devolve `null` com menos de dois pontos — uma linha de um ponto so nao diz
 * nada e um grafico chapado sugeriria estabilidade que ninguem mediu.
 */
export function sparkline(valores: (number | null)[]): SVGElement | null {
  const pontos = valores.filter((v): v is number => v !== null && Number.isFinite(v));
  if (pontos.length < 2) return null;

  const min = Math.min(...pontos);
  const max = Math.max(...pontos);
  const amplitude = max - min || 1;
  const passo = 64 / (pontos.length - 1);
  const coordenadas = pontos
    .map((v, i) => `${(i * passo).toFixed(1)},${(22 - ((v - min) / amplitude) * 20).toFixed(1)}`)
    .join(" ");

  const raiz = svg("svg", {
    class: "spark",
    viewBox: "0 0 64 24",
    preserveAspectRatio: "none",
    "aria-hidden": "true",
  });
  raiz.append(svg("polyline", { points: coordenadas }));
  return raiz;
}
